import React from 'react'

import { useFlag } from 'constants/flags'
import { usePresences } from 'modules/presence/operations'
import { getRenderStatus, preRenderfarmStatus } from 'utils/renderStatus'
import { ShareInfoFragment, ShareListItemFragment } from 'gqlTypes'

import { DocumentGridItem, DocumentListItem } from '../DocumentItem'

export const getLargeShareThumbnailOrFile = (
  share: ShareInfoFragment | ShareListItemFragment
) => {
  if (!share.version?.previewFiles?.length) return null

  const thumbnails = share.version?.previewFiles[0]?.thumbnails || []
  const largeThumbnail = thumbnails.find(x => x?.type === 'L')
  const previewFile = share.version?.previewFiles[0]

  return largeThumbnail ? largeThumbnail : previewFile
}

export interface ConnectedDocumentItemProps {
  share: ShareInfoFragment | ShareListItemFragment
  showProjectName: boolean
  hidePresences?: boolean
  renderDropdownItems?: () => React.ReactNode
  onClick?: React.MouseEventHandler<HTMLElement>
  presentation: 'grid' | 'list'
  userIdentifier?: string
}

const ConnectedDocumentItem = (props: ConnectedDocumentItemProps) => {
  const {
    share,
    showProjectName,
    renderDropdownItems,
    hidePresences = false,
    presentation,
    onClick,
    userIdentifier,
  } = props

  const isCollabEnabled = useFlag('collaborative-editing')
  const isPresenceEnabled = useFlag('presence-v2')

  const renderStatus = share.version?.document?.renderStatus
  const author = share.version?.creator

  const thumbnail = getLargeShareThumbnailOrFile(share)
  const thumbnailSource = thumbnail?.url || undefined

  const canSeePresences =
    isCollabEnabled && isPresenceEnabled && share.userCanAccessVersions

  const { presences = [] } = usePresences(share.identifier, {
    skip: hidePresences || !canSeePresences,
  })

  const hasPresences = presences.length > 0

  const normalizedRenderStatus = getRenderStatus(
    renderStatus || preRenderfarmStatus(!!thumbnail, share.updatedAt!)
  )

  const Component =
    presentation === 'grid' ? DocumentGridItem : DocumentListItem

  return (
    <Component
      name={share.name}
      thumbnailSource={thumbnailSource}
      renderStatus={normalizedRenderStatus}
      isLibrary={share.libraryEnabled || false}
      documentSharingStatus={share.privacyIcon}
      lastUpdatedDate={share.version?.createdAt!}
      renderDropdownItems={renderDropdownItems}
      projectName={showProjectName ? share.project?.name : undefined}
      isEditing={hasPresences}
      onClick={onClick}
      renderAvatarItems={() => {
        if (hasPresences && canSeePresences) {
          return presences.map(({ user }) => ({
            avatar: user.avatarUrl,
            identifier: user.identifier,
            name: userIdentifier === user.identifier ? 'You' : user.name,
          }))
        } else {
          return [
            {
              name: share.version?.userIsCreator ? 'You' : author?.name!,
              identifier: author?.identifier!,
              avatar: author?.avatar?.small!,
            },
          ]
        }
      }}
    />
  )
}

export default ConnectedDocumentItem
